/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 * Licensed under the VIJAYA 1.0 License.
 *
 * NotificationToast — auto-dismissing popup for real-time notifications.
 * (ARCEE-ENH-008 — Real-Time Infrastructure)
 *
 * Appears top-right when a new notification arrives via WebSocket.
 * Disappears automatically after 5 seconds (or on manual close).
 * Priority colours: urgent=red, high=orange, normal=blue, low=grey.
 *
 * Consumes: NotificationContext.latestToast
 * Place once at App root — renders nothing when no toast is pending.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { useNotifications } from '../context/NotificationContext';

const PRIORITY_ACCENT = {
  urgent: '#dc3545',
  high:   '#fd7e14',
  normal: '#0d6efd',
  low:    '#6c757d',
};

const PRIORITY_BG = {
  urgent: '#fff5f5',
  high:   '#fff8f0',
  normal: '#f0f6ff',
  low:    '#f8f9fa',
};

function playSuccessBling() {
  try {
    const AudioCtx = window.AudioContext || window['webkitAudioContext'];
    const ctx = new AudioCtx();

    const play = () => {
      [[784, 0], [880, 0.14], [1047, 0.28]].forEach(([freq, delay]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.type = 'sine';
        o.frequency.setValueAtTime(freq, ctx.currentTime + delay);
        g.gain.setValueAtTime(0.32, ctx.currentTime + delay);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.3);
        o.start(ctx.currentTime + delay);
        o.stop(ctx.currentTime + delay + 0.3);
      });
    };

    // Browsers suspend AudioContext when there has been no recent user gesture.
    // resume() unblocks it; if already running it resolves immediately.
    if (ctx.state === 'suspended') {
      ctx.resume().then(play).catch(() => {});
    } else {
      play();
    }
  } catch (_) {
    // Audio not supported — fail silently
  }
}

export default function NotificationToast() {
  const { latestToast } = useNotifications();
  const { theme }       = useTheme();

  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (!latestToast) return;
    setCurrent(latestToast);
    setVisible(true);
    playSuccessBling();

    const timer = setTimeout(() => setVisible(false), 10000);
    return () => clearTimeout(timer);
  }, [latestToast]);

  if (!visible || !current) return null;

  const accent = PRIORITY_ACCENT[current.priority] || PRIORITY_ACCENT.normal;
  const bg     = PRIORITY_BG[current.priority]     || PRIORITY_BG.normal;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position:     'fixed',
        top:          76,
        right:        20,
        zIndex:       9999,
        minWidth:     300,
        maxWidth:     380,
        background:   bg,
        borderRadius: theme.cardRadius || 10,
        boxShadow:    '0 8px 32px rgba(0,0,0,0.14)',
        borderLeft:   `4px solid ${theme.primary}`,
        padding:      '14px 16px',
        display:      'flex',
        alignItems:   'flex-start',
        gap:          12,
        animation:    'arceeToastIn 0.3s ease',
      }}
    >
      {/* Injected keyframe animation */}
      <style>{`
        @keyframes arceeToastIn {
          from { opacity: 0; transform: translateX(60px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Bell icon */}
      <div
        style={{
          width:          34,
          height:         34,
          borderRadius:   '50%',
          background:     accent + '18',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          flexShrink:     0,
        }}
      >
        <i
          className="bi bi-bell-fill"
          style={{ color: accent, fontSize: '0.9rem' }}
        />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight:   600,
            fontSize:     '0.875rem',
            color:        '#1a1a1a',
            marginBottom: 3,
            overflow:     'hidden',
            textOverflow: 'ellipsis',
            whiteSpace:   'nowrap',
          }}
        >
          {current.title}
        </div>
        <div
          style={{
            fontSize:   '0.8rem',
            color:      '#555',
            lineHeight: 1.4,
            display:    '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow:   'hidden',
          }}
        >
          {current.body}
        </div>
        {current.priority && current.priority !== 'normal' && (
          <span
            style={{
              display:      'inline-block',
              marginTop:    5,
              fontSize:     '10px',
              fontWeight:   700,
              textTransform:'uppercase',
              letterSpacing:'0.5px',
              color:        accent,
              background:   accent + '15',
              padding:      '1px 7px',
              borderRadius: 4,
            }}
          >
            {current.priority}
          </span>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={() => setVisible(false)}
        style={{
          background: 'none',
          border:     'none',
          cursor:     'pointer',
          padding:    '0 0 0 4px',
          color:      '#aaa',
          fontSize:   '0.8rem',
          flexShrink: 0,
          lineHeight: 1,
        }}
        aria-label="Dismiss notification"
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
}
